import { useEffect, useState } from "react";

/**
 * How many columns of a responsively-hidden table are actually on screen.
 *
 * WHY THIS IS NEEDED AT ALL
 * Hiding table columns below a breakpoint is a CSS job (`hidden md:table-cell`)
 * and needs no JavaScript — except for `colSpan`, which is an HTML attribute
 * and cannot be varied by a media query.
 *
 * On All Devices the group-header rows ("Servers (52)") span the table with
 * `colSpan={9}`. I had assumed a browser clamps an over-large colSpan to the
 * columns actually present. It does not: with seven columns hidden, three
 * visible headers totalling 297px still produced an 814px-wide table, because
 * that one colSpan=9 cell was measured at 777px. So the whole point of hiding
 * the columns — a table that fits a phone — was defeated by a group header.
 *
 * The breakpoints are Tailwind's defaults, so a caller can describe its columns
 * the same way it writes the classes and the two cannot drift apart in meaning.
 */

/** Tailwind's default min-widths. */
const BREAKPOINTS = { base: 0, sm: 640, md: 768, lg: 1024, xl: 1280 } as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * `columns` lists the breakpoint at which each column BECOMES visible, in
 * table order. "base" means always visible.
 *
 * Returns the number visible at the current width, which is exactly what a
 * full-width `colSpan` should be.
 */
export function useVisibleColumnCount(columns: Breakpoint[]): number {
  const count = () =>
    typeof window === "undefined"
      ? columns.length // SSR/tests: assume the widest, so nothing is under-spanned
      : columns.filter((bp) => window.innerWidth >= BREAKPOINTS[bp]).length;

  const [visible, setVisible] = useState(count);

  useEffect(() => {
    // One listener on resize rather than a matchMedia per breakpoint: the
    // callback is a cheap arithmetic filter, and setState with an unchanged
    // number is a no-op in React, so a drag-resize does not re-render per pixel.
    const onResize = () => setVisible(count());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.join(",")]);

  return visible;
}
